import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getIcon } from 'sharedComponents/GeneralDialog/helpers';
import { popupTypes } from 'helpers/constants';
import './style.scss';
import { SearchBox } from 'sharedComponents/SearchBox';
import { useTranslation } from 'react-i18next';
import { map } from 'lodash';
import { Checkbox } from 'sharedComponents/Checkbox';
import { IconClose } from 'svgIcons/MotionPortalIcons';
import { getAssetIcon } from 'helpers/icons';

const BulkMuteAssetComponent = ({ bulkAssets = [], data = [], setData, showError, selectedFilters, organization }) => {
    const { t: translate } = useTranslation();
    const [searchFilter, setSearchFilter] = useState('');
    const [muteAssets, setMuteAssets] = useState(bulkAssets);
    const [selectAll, setSelectAll] = useState(false);

    const headerIcon = getIcon(popupTypes.INFO);
    const errorIcon = getIcon(popupTypes.ERROR);
    const selectedSet = new Set(data);

    useEffect(() => setData([]), [setData]);

    useEffect(() => {
        if (muteAssets?.length > 0) {
            setSelectAll(muteAssets.every(asset => selectedSet.has(asset?.id)));
        } else {
            setSelectAll(false);
        }
    }, [muteAssets, data]);

    const handleToggle = useCallback((id) => {
        setData(prevData => prevData.includes(id) ? prevData.filter(item => item !== id) : [...prevData, id]);
    }, [setData]);

    const handleSelectAll = useCallback(() => {
        const filteredIds = new Set(muteAssets.map(asset => asset?.id));
        const allFilteredSelected = muteAssets?.length > 0 && muteAssets.every(asset => selectedSet.has(asset?.id));
        if (allFilteredSelected) {
            setData(prevData => prevData.filter(id => !filteredIds.has(id)));
        } else {
            setData(prevData => {
                const idsToAdd = muteAssets.map(asset => asset?.id).filter(id => !prevData.includes(id));
                return [...prevData, ...idsToAdd];
            });
        }
    }, [muteAssets, setData, data]);

    const handleSearch = useCallback((value) => {
        setSearchFilter(value);
        const lowerCaseValue = value?.toLowerCase();
        setMuteAssets(bulkAssets.filter(({ id, name, serialNumber }) =>
            id?.toString().includes(lowerCaseValue) || name?.toLowerCase().includes(lowerCaseValue) || serialNumber?.toLowerCase().includes(lowerCaseValue)
        ));
    }, [bulkAssets]);

    return (
        <>
            {showError &&
                <div className='bulk-error-icon'>
                    <span className='icon'>{errorIcon}</span>
                    <span>{translate('ABB.Powertrain.Frontend.MuteErrorAssets')}</span>
                </div>
            }
            <div className='bulk-header'>
                <span className='icon'>{headerIcon}</span>
                <span className='title' title={`${translate('ABB.Powertrain.Frontend.MuteAssetList')}: ${selectedFilters?.assetGroup?.name || organization?.name}`}>
                    {translate('ABB.Powertrain.Frontend.MuteAssetList')}: {selectedFilters?.assetGroup?.name || organization?.name}
                </span>
            </div>
            <div className='select-assets'>
                <div className='select-mute-assets'>
                    <h3 className='select-mute-assets__head'>{translate('ABB.Powertrain.Frontend.SelectMuteAssets')}</h3>
                    {bulkAssets?.length > 0 ?
                        <>
                            <div className='search-mute'>
                                <SearchBox
                                    placeholder={translate('ABB.Powertrain.Frontend.searchPlaceholderNameIDSerialNrText')}
                                    onChange={handleSearch}
                                    value={searchFilter}
                                />
                            </div>
                            {muteAssets?.length > 0 ?
                                <div className='mute-asset-list-container'>
                                    <div className='mute-asset-list select-all'>
                                        <Checkbox checked={selectAll} onToggle={handleSelectAll} width={'22px'} height={'22px'} />
                                        <button
                                            className='asset-list-bulk asset-list-bulk--select-all'
                                            onClick={handleSelectAll}
                                            type='button'>{translate('ABB.Powertrain.Frontend.SelectAllAssets')}</button>
                                    </div>
                                    <div className='all-assets-list'>
                                        {map(muteAssets, asset =>
                                            <button
                                                key={asset.id}
                                                type='button'
                                                className='mute-asset-list'
                                                onClick={() => handleToggle(asset?.id)}>
                                                <Checkbox
                                                    checked={selectedSet.has(asset?.id)}
                                                    onToggle={() => handleToggle(asset?.id)}
                                                    width={'22px'}
                                                    height={'22px'}
                                                    isStopPropagation={true}
                                                />
                                                <span className='asset-icon-container checkbox-content'>
                                                    {getAssetIcon(asset?.assetType, { width: 20, height: 20 })}
                                                </span>
                                                <span
                                                    className='asset-list-bulk checkbox-content'
                                                    title={asset.name}
                                                >{asset.name}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                :
                                <div className='no-matches-found'>{translate('ABB.Powertrain.Frontend.NoMuteAssets')}</div>
                            }
                        </>
                        :
                        <div className='no-matches-found'>{translate('ABB.Powertrain.Frontend.NoMuteAssets')}</div>
                    }
                </div>
                <div className='muted-assets'>
                    <h3 className='muted-assets__head'>{translate('ABB.Powertrain.Frontend.selectedMuteAssets')} {data?.length > 0 ? `(${data?.length})` : ''}</h3>
                    {data?.length > 0 ?
                        bulkAssets.filter(asset => selectedSet.has(asset?.id)).map(asset =>
                            <div key={asset?.id} className='mute-asset-list'>
                                <div className='asset-list-bulk'>{asset?.name}</div>
                                <button onClick={() => handleToggle(asset?.id)}>
                                    <IconClose />
                                </button>
                            </div>
                        )
                        :
                        <div className='no-selected-asset'>{translate('ABB.Powertrain.Frontend.NoSelectedMuteAsset')}</div>
                    }
                </div>
            </div>
        </>
    );
};

BulkMuteAssetComponent.propTypes = {
    bulkAssets: PropTypes.arrayOf(PropTypes.object),
    data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    setData: PropTypes.func.isRequired,
    showError: PropTypes.bool,
    selectedFilters: PropTypes.object,
    organization: PropTypes.object
};

export default BulkMuteAssetComponent;
